/**
 * ROUTE PATHS
 * ===========
 * Single source of truth for every route path the app declares. App.js / protectedRoutes.js
 * register these paths and in-app navigation (navigate, <Link>, redirects) refers to them.
 *
 * The sidebar menu itself is not defined here: it comes from GET /api/sidebar (see SidebarContext),
 * which also decides which of these routes a user can see and what they may do on them.
 *
 * Parameterised paths are patterns (":id"); use buildPath() to fill them in.
 */

export const LOGIN_PATH = "/company-login"
export const FORGOT_PASSWORD_PATH = "/company-forgot-password"
export const FORBIDDEN_PATH = "/403"
export const HOME_PATH = "/company/dashboard"
export const UI_PLAYGROUND_PATH = "/dev/ui-playground"

export const PATHS = {
  // Dashboard
  dashboard: HOME_PATH,

  // Administration
  addUser: "/company/administration/add-user",
  editCompanyProfile: "/company/administration/edit-profile",
  userList: "/company/administration/user-list",
  currencies: "/company/administration/currency",
  addCurrency: "/company/administration/add-currency",
  editCurrency: "/company/administration/edit-currency/:currencyId",
  editUser: "/company/administration/edit-user/:userId",
  currencyHistory: "/company/administration/currency-history/:currencyId",
  taxes: "/company/administration/taxes",
  addTax: "/company/administration/add-tax",
  editTax: "/company/administration/edit-tax/:taxId",
  contactMessages: "/company/administration/contact-messages",
  terms: "/company/administration/terms",
  policies: "/company/administration/policies",
  support: "/company/administration/support",

  // Ships and trips
  ships: "/company/ship-trip/ships",
  addShip: "/company/ship-trip/add-ship",
  editShip: "/company/ship-trip/edit-ship/:id",
  trips: "/company/ship-trip/trips",
  addTrip: "/company/ship-trip/add-trip",
  editTrip: "/company/ship-trip/edit-trip/:tripId",

  // Partners
  promotions: "/company/partner-management/promotions",
  addPromotion: "/company/partner-management/add-promotion",
  editPromotion: "/company/partner-management/promotions/:id/edit",
  partners: "/company/partners",
  b2cCustomers: "/company/b2c-customers",
  salesmen: "/company/salesmen",
  markup: "/company/markup",
  addMarkupRule: "/company/markup/add-rule",
  editMarkupRule: "/company/markup/edit/:ruleId",
  commission: "/company/commission",
  addCommissionRule: "/company/commission/add",
  editCommissionRule: "/company/commission/edit/:ruleId",

  // Allocation
  allocations: "/company/partner-management/allocation",
  allocateToChild: "/company/partner-management/allocation/:id/allocate",
  viewAllocation: "/company/partner-management/allocation/:id/view",

  // Sales and booking
  priceList: "/company/pricelist",
  ticketingRules: "/company/ticketing-rules",
  addTicketRule: "/company/add-ticket-rule",
  bookingsAndTickets: "/company/booking-and-tickets",
  createBooking: "/company/create-booking",
  excessBaggageTickets: "/company/excess-baggage-tickets",

  // Checking and boarding
  passengerChecking: "/company/passenger-checking",
  cargoChecking: "/company/cargo-checking",
  vehicleChecking: "/company/vehicle-checking",
  cargoBoarding: "/company/cargo-boarding",
  passengerBoarding: "/company/passenger-boarding",
  vehicleBoarding: "/company/vehicle-boarding",
  tripCompletion: "/company/trip-completion",

  // Finance
  finance: "/company/finance",
  bankCashAccounts: "/company/finance/bank-cash-accounts",
  chartOfAccounts: "/company/finance/chart-of-accounts",
  journalEntries: "/company/finance/journal-entries",
  addJournalEntry: "/company/finance/add-new-journal-entry",
  editJournalEntry: "/company/finance/edit-journal-entry/:id",
  agentTopUps: "/company/finance/agent-top-up-deposits",
  addTopUp: "/company/finance/add-topup",
  paymentsReceipts: "/company/finance/payments-receipts",
  addInternalPaymentReceipt: "/company/finance/add-internal-payment-receipt",
  generalLedger: "/company/finance/general-ledger",
  trialBalance: "/company/finance/trial-balance",

  // Settings
  companyProfile: "/company/settings/company-profile",
  companyProfileList: "/company/settings/company-profile-list",
  rolePermissions: "/company/settings/role-permission",
  addGroupPermission: "/company/settings/add-group-permission",
  ports: "/company/settings/port",
  addPort: "/company/settings/add-port",
  editPort: "/company/settings/port/:id",
  cabins: "/company/settings/cabin",
  editCabin: "/company/settings/cabin/:cabinId",
  addCabin: "/company/settings/add-cabin",
  payloadTypes: "/company/settings/payload-type",

  // System alerts
  systemAlerts: "/company/system-alerts",
}

/**
 * Fill a path pattern's ":param" segments, e.g.
 *   buildPath(PATHS.editTrip, { tripId: trip._id }) -> "/company/ship-trip/edit-trip/66f..."
 *
 * Values are interpolated exactly as a template literal would (no encoding), so links built
 * here are identical to the hand-written `/.../${id}` strings they replace.
 */
export function buildPath(pattern, params = {}) {
  return pattern.replace(/:([A-Za-z0-9_]+)/g, (_match, name) => {
    if (!(name in params)) throw new Error(`buildPath: missing "${name}" for ${pattern}`)
    return `${params[name]}`
  })
}
